// Google Analytics 4 - RGPD compliant with Google Consent Mode v2
// Measurement ID se carga desde variable de entorno con fallback al ID solicitado.
// Importante: NUNCA se envían PII (nombre, email, teléfono, mensajes).

const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-RXK0PHYZ82';

const CONSENT_STORAGE_KEY = 'cookie-consent-ga4';
const INTERNAL_FLAG_KEY = 'ga_internal';
const UTM_STORAGE_KEY = 'ga_utm';
let isGaScriptLoaded = false;

const ensureDataLayer = () => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
};

const loadGaScript = () => {
  if (isGaScriptLoaded || typeof document === 'undefined') return;
  const existing = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js"]`
  );
  if (existing) {
    isGaScriptLoaded = true;
    return;
  }
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  isGaScriptLoaded = true;
};

// Lee UTM del querystring y los persiste 30 días. First-touch attribution.
const captureUtmParams = () => {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const current = {};
    let hasUtm = false;
    keys.forEach((k) => {
      const v = params.get(k);
      if (v) {
        current[k] = v.slice(0, 100); // cap length
        hasUtm = true;
      }
    });
    if (hasUtm) {
      const payload = { ...current, _ts: Date.now() };
      window.localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
      return current;
    }
    // Si no hay UTM nuevas, devolver las guardadas si no han expirado
    const stored = window.localStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
      if (data._ts && Date.now() - data._ts < THIRTY_DAYS) {
        const { _ts, ...utms } = data;
        return utms;
      }
      window.localStorage.removeItem(UTM_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
  return {};
};

export const getStoredUtm = () => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = window.localStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return {};
    const { _ts, ...utms } = JSON.parse(stored);
    return utms;
  } catch {
    return {};
  }
};

// Marca el tráfico como interno (Fabio o pruebas). Usar en la consola del navegador:
//   localStorage.setItem('ga_internal', 'true')
// Para quitarlo:
//   localStorage.removeItem('ga_internal')
const isInternalTraffic = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(INTERNAL_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
};

export const initGA = () => {
  if (typeof window === 'undefined') return;
  ensureDataLayer();

  // Defaults denegados ANTES de cargar el script
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });

  loadGaScript();

  window.gtag('js', new Date());
  const configParams = {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  };
  // Marca tráfico interno (Fabio / pruebas) para poder filtrarlo en GA4
  if (isInternalTraffic()) {
    configParams.traffic_type = 'internal';
    configParams.debug_mode = true;
  }
  window.gtag('config', GA_MEASUREMENT_ID, configParams);

  // Captura UTM al cargar
  captureUtmParams();

  // Si el usuario ya aceptó previamente, actualiza el consentimiento
  const stored = getStoredConsent();
  if (stored === 'granted') {
    updateConsent(true);
  }
};

export const getStoredConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredConsent = (value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
};

export const updateConsent = (granted) => {
  ensureDataLayer();
  const state = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: state,
  });
};

// Envía un evento solo si el usuario aceptó. Sin PII.
// Añade automáticamente traffic_type='internal' si corresponde.
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined') return;
  if (getStoredConsent() !== 'granted') return;
  if (!window.gtag) return;
  const finalParams = { ...params };
  if (isInternalTraffic()) {
    finalParams.traffic_type = 'internal';
  }
  window.gtag('event', eventName, finalParams);
};

// Helpers específicos. 'service' es opcional y controlado (nunca PII).
export const trackWhatsAppClick = (location, service = null) => {
  const params = { location: String(location || 'unknown') };
  if (service) params.service = String(service).slice(0, 60);
  trackEvent('whatsapp_click', params);
};

export const trackPhoneClick = (location) => {
  trackEvent('phone_click', { location: String(location || 'unknown') });
};

export const trackContactFormSubmit = (service = null) => {
  const params = {};
  if (service) params.service = String(service).slice(0, 60);
  trackEvent('contact_form_submit', params);
};
